import React from 'react';
import {useIntl} from 'react-intl';
import {useProjectTotals} from './ProjectScopingTotalsContext';
import {TileRow} from './totals-header-box-tiles/Tile_styled';
import {Title, Data} from './ProjectScopingTotals_styled';
import {ValueCell} from '../../../../../components/initial-plan/ValueCell';

interface TotalsRowProps {
	totalsField: string;
	translationId: string;
}

const MoneyRow: React.FC<TotalsRowProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData, settings} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell
					noJustify
					type={ValueCell.VALUE_TYPE.PRICE}
					value={totalsData[totalsField] || 0}
					currencySymbol={settings.currencySymbol}
				/>
			</Data>
		</TileRow>
	);
};

const TimeRow: React.FC<TotalsRowProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.TIME} value={totalsData[totalsField] || 0} />
			</Data>
		</TileRow>
	);
};

const PercentageRow: React.FC<TotalsRowProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.PERCENTAGE} value={totalsData[totalsField] || 0} />
			</Data>
		</TileRow>
	);
};

const PointsRow: React.FC<TotalsRowProps> = ({totalsField, translationId}) => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: translationId})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.POINTS} value={totalsData[totalsField] || 0} />
			</Data>
		</TileRow>
	);
};

const PlannedHoursRow: React.FC = () => {
	const {projectData} = useProjectTotals();

	return projectData.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<TimeRow totalsField="allocatedHours" translationId="project_budget.allocated_hours" />
	) : projectData.useBaseline ? (
		<TimeRow totalsField="baselineHours" translationId="baseline.hours" />
	) : (
		<TimeRow totalsField="estimatedHours" translationId="common.estimated_hours" />
	);
};

const ActualHoursRow: React.FC = () => (
	<TimeRow totalsField="actualHours" translationId="project_settings.financials.value_calculations.actual_hours" />
);

const RemainingWorkHoursRow: React.FC = () => (
	<TimeRow totalsField="remainingHours" translationId="common.remaining_work_hours" />
);

const ProjectedTotalHoursRow: React.FC = () => (
	<TimeRow totalsField="projectedTotalHours" translationId="common.projected_total_hours" />
);

const EstimatedCostRow: React.FC = () => <MoneyRow totalsField="plannedCost" translationId="common.estimated_cost" />;

const BaselineCostRow: React.FC = () => <MoneyRow totalsField="baselineCost" translationId="common.baseline_cost" />;

const PlannedCostRow: React.FC = () => {
	const {projectData} = useProjectTotals();

	return projectData.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<MoneyRow totalsField="plannedCost" translationId="common.estimated_cost" />
	) : projectData.useBaseline ? (
		<MoneyRow totalsField="baselineCost" translationId="common.baseline_cost" />
	) : (
		<MoneyRow totalsField="plannedCost" translationId="common.estimated_cost" />
	);
};

const ActualCostRow: React.FC = () => <MoneyRow totalsField="actualCost" translationId="common.actual_cost" />;

const ProjectedTotalCostRow: React.FC = () => (
	<MoneyRow totalsField="projectedTotalCost" translationId="common.projected_total_cost" />
);

const ProgressRow: React.FC = () => <PercentageRow totalsField="progress" translationId="common.project_progress" />;

const ActualNonBillableHoursRow: React.FC = () => (
	<TimeRow totalsField="actualNonBillableHours" translationId="common.actual_non_billable_hours" />
);

const ProjectedTotalNonBillableHoursRow: React.FC = () => (
	<TimeRow totalsField="projectedNonBillableHours" translationId="common.projected_total_non_billable_hours" />
);

const ProjectedTotalHoursPercentageBillableRow: React.FC = () => {
	const intl = useIntl();
	const {totalsData} = useProjectTotals();
	const {projectedBillableHours, projectedTotalHours} = totalsData;
	const percentageBillableHours = projectedTotalHours > 0 ? projectedBillableHours / projectedTotalHours : 0;
	return (
		<TileRow>
			<Title>{intl.formatMessage({id: 'common.projected_total_hours_percentage_billable'})}</Title>
			<Data small>
				<ValueCell noJustify type={ValueCell.VALUE_TYPE.PERCENTAGE} value={percentageBillableHours} />
			</Data>
		</TileRow>
	);
};

const EstimatedRevenueRow: React.FC = () => <MoneyRow totalsField="plannedRevenue" translationId="common.estimated_revenue" />;

const BaselineRevenueRow: React.FC = () => <MoneyRow totalsField="baselineRevenue" translationId="common.baseline_revenue" />;

const PlannedRevenueRow: React.FC = () => {
	const {projectData} = useProjectTotals();

	return projectData.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<MoneyRow totalsField="plannedRevenue" translationId="common.estimated_revenue" />
	) : projectData.useBaseline ? (
		<MoneyRow totalsField="baselineRevenue" translationId="common.baseline_revenue" />
	) : (
		<MoneyRow totalsField="plannedRevenue" translationId="common.estimated_revenue" />
	);
};

const EstimatedProfitRow: React.FC = () => <MoneyRow totalsField="plannedProfit" translationId="common.estimated_profit" />;

const BaselineProfitRow: React.FC = () => <MoneyRow totalsField="baselineProfit" translationId="common.baseline_profit" />;

const PlannedProfitRow: React.FC = () => {
	const {projectData} = useProjectTotals();

	return projectData.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<MoneyRow totalsField="plannedProfit" translationId="common.estimated_profit" />
	) : projectData.useBaseline ? (
		<MoneyRow totalsField="baselineProfit" translationId="common.baseline_profit" />
	) : (
		<MoneyRow totalsField="plannedProfit" translationId="common.estimated_profit" />
	);
};

const EstimatedMarginRow: React.FC = () => (
	<PercentageRow totalsField="plannedMargin" translationId="common.estimated_margin" />
);

const BaselineMarginRow: React.FC = () => <PercentageRow totalsField="baselineMargin" translationId="common.baseline_margin" />;

const PlannedMarginRow: React.FC = () => {
	const {projectData} = useProjectTotals();

	return projectData.useAllocationsForPlannedValues && projectData.hasAllocations ? (
		<PercentageRow totalsField="plannedMargin" translationId="common.estimated_margin" />
	) : projectData.useBaseline ? (
		<PercentageRow totalsField="baselineMargin" translationId="common.baseline_margin" />
	) : (
		<PercentageRow totalsField="plannedMargin" translationId="common.estimated_margin" />
	);
};

const ProjectedTotalRevenueRow: React.FC = () => (
	<MoneyRow totalsField="projectedTotalRevenue" translationId="common.projected_total_revenue" />
);

const ProjectedTotalProfitRow: React.FC = () => (
	<MoneyRow totalsField="projectedTotalProfit" translationId="common.projected_total_profit" />
);

const ProjectedTotalMarginRow: React.FC = () => (
	<PercentageRow totalsField="projectedTotalMargin" translationId="common.projected_total_margin" />
);

const EstimatedPointsRow: React.FC = () => <PointsRow totalsField="estimatedPoints" translationId="common.estimated_points" />;

const CompletedPointsRow: React.FC = () => <PointsRow totalsField="completedPoints" translationId="common.completed_points" />;

const RemainingPointsRow: React.FC = () => (
	<PointsRow totalsField="remainingPoints" translationId="common.remaining_work_points" />
);

const RemainingCostRow: React.FC = () => <MoneyRow totalsField="remainingCost" translationId="common.remaining_work_cost" />;

export const ProjectScopingTotalsRows = {
	PlannedHours: PlannedHoursRow,
	ActualHours: ActualHoursRow,
	RemainingWorkHours: RemainingWorkHoursRow,
	ProjectedTotalHours: ProjectedTotalHoursRow,
	EstimatedCost: EstimatedCostRow,
	BaselineCost: BaselineCostRow,
	PlannedCost: PlannedCostRow,
	ActualCost: ActualCostRow,
	ProjectedTotalCost: ProjectedTotalCostRow,
	Progress: ProgressRow,
	ActualNonBillableHours: ActualNonBillableHoursRow,
	ProjectedTotalNonBillableHours: ProjectedTotalNonBillableHoursRow,
	ProjectedTotalHoursPercentageBillable: ProjectedTotalHoursPercentageBillableRow,
	EstimatedRevenue: EstimatedRevenueRow,
	BaselineRevenue: BaselineRevenueRow,
	PlannedRevenue: PlannedRevenueRow,
	EstimatedProfit: EstimatedProfitRow,
	BaselineProfit: BaselineProfitRow,
	PlannedProfit: PlannedProfitRow,
	EstimatedMargin: EstimatedMarginRow,
	BaselineMargin: BaselineMarginRow,
	PlannedMargin: PlannedMarginRow,
	ProjectedTotalRevenue: ProjectedTotalRevenueRow,
	ProjectedTotalProfit: ProjectedTotalProfitRow,
	ProjectedTotalMargin: ProjectedTotalMarginRow,
	EstimatedPoints: EstimatedPointsRow,
	CompletedPoints: CompletedPointsRow,
	RemainingPoints: RemainingPointsRow,
	RemainingCost: RemainingCostRow,
};
